/**
 * klubb-cart — full-page cart behavior.
 *
 * - Quantity stepper (+/−) and manual input update line item quantity.
 * - Remove button sets quantity to 0.
 * - All updates POST to /cart/change.js (Shopify Cart API), then the
 *   page reloads so prices, totals, and any remaining lines re-render
 *   server-side. Reload is acceptable for v1 — most carts are short
 *   enough that the round-trip isn't perceptible.
 *
 * Exposes:
 *   wireLine(lineEl, onUpdate?) — for tests + auto-init (called for
 *     each [data-klubb-cart-line] in the page). onUpdate is optional;
 *     when omitted, the default behavior is to call updateCart() which
 *     hits /cart/change.js and reloads on success.
 */

// Set by klubb-cart-xsell.js right before its post-add reload; consumed once
// so the reloaded cart reveals the just-added line instead of hiding it
// behind the mobile expander / desktop scroll cap.
function consumeXsellAddedFlag() {
  try {
    if (sessionStorage.getItem('klubb:xsell-added') !== '1') return false;
    sessionStorage.removeItem('klubb:xsell-added');
    return true;
  } catch (e) { return false; }
}

export function wireLine(line, onUpdate, onRemoveGroup, onUpdateGroup) {
  // Guard against double-wiring (e.g. auto-init + an explicit caller).
  if (line.dataset.klubbCartWired === 'true') return;
  line.dataset.klubbCartWired = 'true';

  const input = line.querySelector('[data-klubb-qty-input]');
  const decBtn = line.querySelector('[data-klubb-qty-step="-1"]');
  const incBtn = line.querySelector('[data-klubb-qty-step="1"]');
  const removeBtn = line.querySelector('[data-klubb-cart-remove]');
  const lineKey = line.getAttribute('data-line-key');
  if (!lineKey) return;

  // One request per line at a time. A second click while the first request was
  // in flight used to race it (two reloads, lost update), so the line locks
  // until the request settles: on success the page reloads anyway, on failure
  // the controls come back.
  function pending() { return line.getAttribute('aria-busy') === 'true'; }
  function setBusy(on) {
    if (on) line.setAttribute('aria-busy', 'true'); else line.removeAttribute('aria-busy');
    [incBtn, decBtn, removeBtn].forEach((btn) => { if (btn) btn.disabled = on; });
    if (input) input.disabled = on;
  }
  function track(result) {
    setBusy(true);
    const unlock = () => setBusy(false);
    Promise.resolve(result).then(unlock, unlock);
  }

  function emit(qty) {
    // D43: a personalized garment carries a `_print_group`; its read-only Trykk
    // charge lines must follow the garment's quantity. When the garment qty
    // changes and the group has linked lines, re-sync the WHOLE group to the new
    // qty in one transaction. Lone lines (no group, or group of one) update solo.
    const group = line.getAttribute('data-klubb-print-group');
    if (qty > 0 && group && typeof onUpdateGroup === 'function') {
      const keys = Array.from(document.querySelectorAll('[data-klubb-print-group="' + group + '"][data-line-key]'))
        .map((l) => l.getAttribute('data-line-key'))
        .filter(Boolean);
      if (keys.length > 1) { track(onUpdateGroup(keys, qty)); return; }
    }
    if (typeof onUpdate === 'function') track(onUpdate(lineKey, qty));
  }

  function step(delta) {
    if (pending()) return;
    const current = parseInt(input.value, 10) || 0;
    const next = current + delta;
    // Stepping below 1 is a no-op — use Remove to delete the line.
    if (next < 1) return;
    input.value = String(next);
    emit(next);
  }

  if (incBtn) incBtn.addEventListener('click', () => step(1));
  if (decBtn) decBtn.addEventListener('click', () => step(-1));

  if (input) {
    input.addEventListener('change', () => {
      if (pending()) return;
      let qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 1) qty = 1;
      input.value = String(qty);
      emit(qty);
    });
  }

  if (removeBtn) {
    removeBtn.addEventListener('click', () => {
      if (pending()) return;
      // D41: a personalized garment carries a `_print_group`; removing it must
      // also remove its linked Trykk charge lines. Cascade to every line in the
      // group (no orphan charges). Plain lines just remove themselves.
      const group = line.getAttribute('data-klubb-print-group');
      if (group && typeof onRemoveGroup === 'function') {
        const keys = Array.from(document.querySelectorAll('[data-klubb-print-group="' + group + '"][data-line-key]'))
          .map((l) => l.getAttribute('data-line-key'))
          .filter(Boolean);
        if (keys.length) { track(onRemoveGroup(keys)); return; }
      }
      emit(0);
    });
  }
}

// A player package is an indivisible unit — its only cart action is "remove the
// whole package". Collect every component line key under the bundle and zero
// them out in one request.
export function wireBundle(bundle, onRemove) {
  if (bundle.dataset.klubbBundleWired === 'true') return;
  bundle.dataset.klubbBundleWired = 'true';

  const removeBtn = bundle.querySelector('[data-klubb-bundle-remove]');
  if (!removeBtn) return;

  removeBtn.addEventListener('click', () => {
    if (bundle.getAttribute('aria-busy') === 'true') return;
    const keys = Array.from(bundle.querySelectorAll('[data-klubb-cart-line][data-line-key]'))
      .map((line) => line.getAttribute('data-line-key'))
      .filter(Boolean);
    if (keys.length === 0) return;
    if (typeof onRemove !== 'function') return;
    // Same in-flight lock as the lines: one remove request per package.
    bundle.setAttribute('aria-busy', 'true');
    removeBtn.disabled = true;
    const unlock = () => { bundle.removeAttribute('aria-busy'); removeBtn.disabled = false; };
    Promise.resolve(onRemove(keys)).then(unlock, unlock);
  });
}

// Remove several lines atomically (/cart/update.js takes a key→quantity map),
// so a package never half-removes if one /change.js call were to fail.
export function removeLines(lineKeys) {
  const updates = {};
  lineKeys.forEach((key) => { updates[key] = 0; });
  return fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates: updates }),
  })
    .then((res) => {
      if (!res.ok) throw new Error('Cart update failed: ' + res.status);
      return res.json();
    })
    .then(() => {
      window.location.reload();
    })
    .catch((err) => {
      if (typeof console !== 'undefined' && console.error) console.error('[klubb-cart]', err);
    });
}

// D43: set several lines to the SAME quantity atomically (/cart/update.js takes
// a key→quantity map). Used to keep a garment's Trykk charge lines in lockstep
// with the garment quantity when it changes in the cart.
export function updateLines(lineKeys, quantity) {
  const updates = {};
  lineKeys.forEach((key) => { updates[key] = quantity; });
  return fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates: updates }),
  })
    .then((res) => {
      if (!res.ok) throw new Error('Cart update failed: ' + res.status);
      return res.json();
    })
    .then(() => {
      window.location.reload();
    })
    .catch((err) => {
      if (typeof console !== 'undefined' && console.error) console.error('[klubb-cart]', err);
    });
}

export function updateCart(lineKey, quantity) {
  return fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity: quantity }),
  })
    .then((res) => {
      if (!res.ok) throw new Error('Cart update failed: ' + res.status);
      return res.json();
    })
    .then(() => {
      window.location.reload();
    })
    .catch((err) => {
      if (typeof console !== 'undefined' && console.error) console.error('[klubb-cart]', err);
    });
}

// Cross-sell fold guarantee (desktop): the CSS ships a conservative baseline
// cap; sync() below replaces it with the exact summary-column height so the
// two columns always bottom-align. When the list actually overflows, the
// wrapper is flagged so the bottom fade + "N varer" pill render. No-JS keeps
// the baseline CSS cap.
export function initItemsScroll(wrap) {
  // Guard against double-wiring, same convention as wireLine/wireBundle.
  if (wrap.dataset.klubbItemsScrollWired === 'true') return;
  wrap.dataset.klubbItemsScrollWired = 'true';

  const scroller = wrap.querySelector('[data-klubb-items-scroll]');
  if (!scroller) return;
  const summary = document.querySelector('.klubb-cart__summary');

  function check() {
    // +1 absorbs sub-pixel rounding of scrollHeight vs clientHeight.
    wrap.classList.toggle('has-overflow', scroller.scrollHeight > scroller.clientHeight + 1);
  }

  // Desktop (two-column layout): cap the list at exactly the summary
  // column's height — no dead space on tall windows, no viewport constants.
  // The rect delta subtracts whatever sits above the scroller inside the
  // column (head row). Mobile/stacked (≤1080px) clears the cap.
  function sync() {
    // matchMedia shares the exact CSS breakpoint evaluation (innerWidth can
    // disagree at fractional zoom); fall back to innerWidth without it.
    const stacked = typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 1080px)').matches
      : window.innerWidth <= 1080;
    let next = '';
    if (summary && !stacked) {
      const offset = scroller.getBoundingClientRect().top - wrap.getBoundingClientRect().top;
      next = Math.max(160, summary.offsetHeight - offset) + 'px';
    }
    // Write only on change: sync() resizes an element the ResizeObserver
    // watches, and an unconditional write triggers the benign-but-noisy
    // "RO loop" warning. The value settles in one pass (inputs are
    // independent of the scroller's own size).
    if (scroller.style.maxHeight !== next) scroller.style.maxHeight = next;
    check();
  }
  sync();
  // ResizeObserver (same pattern as klubb-product-showcase.js) also catches
  // content reflows — image loads, font swaps; resize is the no-RO fallback.
  // Observing wrap covers head-row height changes (they shift the offset).
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(sync);
    ro.observe(scroller);
    ro.observe(wrap);
    if (summary) ro.observe(summary);
  } else {
    window.addEventListener('resize', sync);
  }
}

// Mobile fold guarantee: >2 top-level line blocks collapse to the first 2
// behind a "Vis alle N varer" button (CSS confines the visual effect to
// ≤1080px). A spillerpakke bundle counts as ONE block but renders very tall,
// so even a 3-block cart needs the collapse. Expansion is one-way — the list
// stays open until next page load.
export function wireItemsToggle(wrap, opts) {
  // Guard against double-wiring, same convention as wireLine/wireBundle.
  if (wrap.dataset.klubbItemsToggleWired === 'true') return;
  wrap.dataset.klubbItemsToggleWired = 'true';

  const btn = wrap.querySelector('[data-klubb-items-toggle]');
  const scroller = wrap.querySelector('[data-klubb-items-scroll]');
  if (!btn || !scroller) return;
  if (scroller.children.length <= 2) { btn.hidden = true; return; }
  if (opts && opts.keepOpen) { btn.hidden = true; return; }
  wrap.classList.add('is-collapsible', 'is-collapsed');
  btn.addEventListener('click', () => {
    wrap.classList.remove('is-collapsed');
    btn.setAttribute('aria-expanded', 'true');
    btn.hidden = true;
    // The focused button just vanished — anchor keyboard/SR users in the
    // newly revealed list instead of dropping them back to <body>.
    scroller.focus();
  });
}

function init() {
  // Only the cart PAGE's lines (inside [data-klubb-cart]): wired to update-then-reload. The cart drawer's
  // lines (D66, assets/klubb-cart-drawer.js) sit on every page and update in place with their own handler.
  document.querySelectorAll('[data-klubb-cart] [data-klubb-cart-line]').forEach((line) => {
    wireLine(line, updateCart, removeLines, updateLines);
  });
  document.querySelectorAll('[data-klubb-cart] [data-klubb-bundle]').forEach((bundle) => {
    wireBundle(bundle, removeLines);
  });
  const revealAdded = consumeXsellAddedFlag();
  document.querySelectorAll('[data-klubb-cart-items]').forEach((wrap) => {
    initItemsScroll(wrap);
    wireItemsToggle(wrap, { keepOpen: revealAdded });
    if (revealAdded) {
      const scroller = wrap.querySelector('[data-klubb-items-scroll]');
      if (scroller) scroller.scrollTop = scroller.scrollHeight;
    }
  });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
